/**
 * Single-language UI. The whole interface renders in ONE language chosen by the
 * user (or detected from the browser) — never both at once.
 */

import { VI } from "./i18n-vi";

/**
 * Vietnamese since 2026-10-23: Vietnam is the second-largest xiangqi community,
 * and a reader arriving from /vi used to land in English. The interface is
 * translated (i18n-vi.ts); lesson and puzzle text, which comes in {zh, en}
 * pairs, is shown in English to a Vietnamese reader until it has a vi half.
 */
export type Lang = "zh" | "en" | "vi";

/**
 * The content site's locale, which is NOT the same set as the app's.
 *
 * This interface exists in Simplified and English only, and there is no reason
 * to fork it — a Traditional reader reads Simplified without difficulty. The
 * content site does have a Traditional tree, though, so a reader who arrived
 * from /zh-hant should be sent back there when the app links out to the terms,
 * the privacy notice or a rules page. So: one language for the UI, and a
 * separate memory of which tree the reader came from.
 */
export type SiteLocale = "en" | "zh" | "zh-hant" | "vi";

const KEY = "xq-lang";
const SITE_KEY = "xq-site-locale";

/** The app UI language a site locale maps to. Traditional reads Simplified. */
const uiFor = (l: SiteLocale): Lang => (l === "en" ? "en" : l === "vi" ? "vi" : "zh");

const isSiteLocale = (v: string | null): v is SiteLocale => v === "zh" || v === "en" || v === "zh-hant" || v === "vi";

/** Vietnam's zones. Many Vietnamese phones and PCs run an English browser, so the
 *  timezone is the second signal, as on the content site (lang-suggest.mjs). */
const VI_ZONES = ["Asia/Ho_Chi_Minh", "Asia/Saigon"];

function detect(): SiteLocale {
  // An explicit ?lang= wins — the content site appends it to every link into
  // this app, so the visitor's choice carries across the origin boundary.
  try {
    const params = new URLSearchParams(location.search);
    const q = params.get("lang");
    if (isSiteLocale(q)) {
      localStorage.setItem(SITE_KEY, q);
      localStorage.setItem(KEY, uiFor(q));
      // Consume it: if the parameter stayed in the URL it would override the
      // user's own choice on every single reload.
      params.delete("lang");
      const rest = params.toString();
      history.replaceState(null, "", location.pathname + (rest ? `?${rest}` : ""));
      return q;
    }
  } catch {
    /* ignore */
  }
  const savedSite = localStorage.getItem(SITE_KEY);
  if (isSiteLocale(savedSite)) return savedSite;
  const saved = localStorage.getItem(KEY);
  if (saved === "zh" || saved === "en" || saved === "vi") return saved;
  const nav = (navigator.language || "en").toLowerCase();
  if (nav.startsWith("zh")) return "zh";
  if (nav.startsWith("vi")) return "vi";
  try {
    if (VI_ZONES.includes(Intl.DateTimeFormat().resolvedOptions().timeZone)) return "vi";
  } catch {
    /* ignore */
  }
  // The address, as /a/geo last answered (see checkGeo below).
  try {
    const g = JSON.parse(localStorage.getItem(GEO_KEY) || "null") as { vn?: boolean; t?: number } | null;
    if (g?.vn) return "vi";
  } catch {
    /* ignore */
  }
  guessed = true;
  return "en";
}

/** The visitor's address said Vietnam (or not), cached for a week. */
/** v2: the first table missed VPN exits in Vietnam, and its "no" answers were cached. */
const GEO_KEY = "xq-geo2";
const CHOSEN_KEY = "xq-lang-chosen";
/** True when nothing chose the language: no saved choice, no ?lang=, and an English browser. */
let guessed = false;

let siteLocale: SiteLocale = detect();
let lang: Lang = uiFor(siteLocale);

/**
 * Nothing on the device said Vietnam, so ask the server whether the address
 * does (services/realtime/src/geo.ts: APNIC registry data, yes/no only). The
 * owner asked that anyone coming from Vietnam get Vietnamese, including on an
 * English browser or through a VPN. Once a week at most; and on a yes the app
 * switches and reloads once, so the page is never half in each language.
 */
function checkGeo(): void {
  if (!guessed) return;
  try {
    const g = JSON.parse(localStorage.getItem(GEO_KEY) || "null") as { t?: number; vn?: boolean } | null;
    // A yes is kept a week, a no a day.
    if (g?.t && g.t > Date.now() - (g.vn ? 7 : 1) * 864e5) return;
  } catch {
    /* ignore */
  }
  fetch("/a/geo", { credentials: "omit", cache: "no-store" })
    .then((r) => r.json())
    .then((j: { vn?: boolean }) => {
      try { localStorage.setItem(GEO_KEY, JSON.stringify({ vn: !!j.vn, t: Date.now() })); } catch { /* ignore */ }
      if (j.vn && lang === "en" && !localStorage.getItem(CHOSEN_KEY)) {
        setLang("vi", true);
        location.reload();
      }
    })
    .catch(() => { /* offline or blocked: stay as we are */ });
}
try { checkGeo(); } catch { /* no fetch (worker) */ }

/** <html lang>: screen readers, hyphenation, and the :lang(vi) font rule in index.html. */
const HTML_LANG: Record<Lang, string> = { zh: "zh-Hans", en: "en", vi: "vi" };
function markDocument(): void {
  try { document.documentElement.lang = HTML_LANG[lang]; } catch { /* no document (worker) */ }
}
markDocument();

export function getLang(): Lang {
  return lang;
}

/** Which tree of the content site to link back to. */
export function getSiteLocale(): SiteLocale {
  return siteLocale;
}

/** Whether the reader picked the language themselves (Settings), rather than it being detected. */
export function langChosen(): boolean {
  try { return localStorage.getItem(CHOSEN_KEY) === "1"; } catch { return false; }
}

export function setLang(l: Lang, auto = false) {
  lang = l;
  markDocument();
  localStorage.setItem(KEY, l);
  if (!auto) { try { localStorage.setItem(CHOSEN_KEY, "1"); } catch { /* ignore */ } }
  // Choosing the UI language in the app is a statement about the content site
  // too — except that picking 中文 should not demote a Traditional reader to
  // the Simplified tree when that is already where they came from.
  if (!(l === "zh" && siteLocale === "zh-hant")) {
    siteLocale = l;
    localStorage.setItem(SITE_KEY, l);
  }
}

type Dict = Record<string, string>;

const ZH: Dict = {
  // home
  player: "玩家",
  nickname: "昵称",
  language: "语言",
  scene: "场景",
  sceneDay: "日",
  sceneNight: "夜",
  weatherClear: "晴",
  weatherRain: "雨",
  weatherSnow: "雪",
  pieces: "棋子",
  piecesTrad: "繁體",
  piecesSimp: "简体",
  piecesWestern: "字母",
  piecesSymbol: "符号",
  soundOn: "🔊 声音",
  soundOff: "🔇 静音",
  playBot: "与电脑对弈",
  playBotSub: "对战强力引擎 · 五种难度 · 可提示",
  playOnline: "在线匹配",
  playOnlineSub: "与陌生棋友即时对局",
  playFriends: "好友对战",
  playFriendsSub: "创建私人房间，分享链接",
  lessons: "教程",
  lessonsSub: "循序渐进学象棋",
  homeNote: "拖动或点击棋子走子；受攻击的棋子会被标记。",

  // bot setup
  botTitle: "选一个对手",
  difficulty: "难度",
  yourSide: "执子",
  beginner: "启蒙",
  easy: "入门",
  medium: "中级",
  hard: "高手",
  master: "大师",
  sideRed: "执红",
  sideBlack: "执黑",
  sideRandom: "随机",
  back: "返回",
  start: "开始",

  // friends / online
  friendsTitle: "好友对战",
  friendsDesc: "创建房间并把链接发给好友，或输入房间号加入。",
  createRoom: "创建房间",
  roomCodePlaceholder: "房间号",
  join: "加入",
  searching: "匹配中…",
  creating: "创建中…",
  joining: "加入房间",
  cancel: "取消",
  /* 反馈 */
  fbTitle: "反馈意见",
  fbFromGame: "这局有问题？告诉我们",
  fbSub: "告诉我们哪里出错、缺了什么，或者哪里好用。每一条我们都会看。",
  fbOpen: "写给我们",
  fbWhat: "你的留言",
  fbBug: "出了问题",
  fbIdea: "一个建议",
  fbPraise: "好评",
  fbOther: "其他",
  fbPlaceholder: "发生了什么？你本来以为会怎样？",
  fbEmailHint: "邮箱，如果希望收到回复（可不填）",
  fbSend: "发送",
  fbSending: "正在发送…",
  fbThanks: "谢谢，我们收到了。",
  fbEmpty: "请先写点什么。",
  fbFailed: "没发送成功。你的留言还在，再试一次。",
  fbDiscordTitle: "加入我们的 Discord 社区",
  fbDiscordText: "和棋友聊天，有问题也能直接告诉我们。",
  fbDiscordJoin: "加入",
  /* the timed prompt, and the rating it asks for */
  fbAskTitle: "能占用你半分钟吗？",
  fbAskSub: "想听听你的想法：哪里好用，哪里不好用。不到 30 秒。",
  fbAskYes: "写几句",
  fbAskLater: "现在不用",
  fbAskNever: "不用了，谢谢",
  fbRate: "整体感觉如何？",
  fbRateSkip: "（可不填）",
  fbRate1: "很差",
  fbRate5: "很好",
  fbImprove: "我们该改进什么？",
  groupFeedback: "反馈",
  groupFeedbackSub: "告诉我们哪里不对或缺了什么",
  roomLabel: "房间号",
  shareLink: "把链接发给好友：",
  copy: "复制",
  copiedShort: "已复制",
  chatEmpty: "还没有人说话。打个招呼吧。",
  watchingCount: "{n} 人观战",
  waitingOpponent: "等待对手加入…",

  // game
  redToMove: "红方走棋",
  blackToMove: "黑方走棋",
  gameOver: "对局结束",
  yourMove: "轮到你",
  opponentMove: "对手思考中",
  botThinking: "电脑思考中…",
  check: "将军！",
  // the warning before a repetition loses the game (apps/web/src/repwarn.ts)
  repWarnCheckShort: "长将，请变着",
  repWarnChaseShort: "长捉，请变着",
  repWarnCheck: "长将：你每步都在将军，局面已经重复。再重复一次就判你负（WXF 规则：长将判负）。请变着。",
  repWarnChase: "长捉：你每步都在捉同一个子，局面已经重复。再重复一次就判你负（WXF 规则：长捉判负）。请变着。",
  hint: "提示",
  hintThinking: "分析中…",
  undo: "悔棋",
  newGame: "新局",
  home: "退出",
  resign: "认输",
  rematch: "再战",
  movesTitle: "着法",
  you: "你",
  bot: "电脑",
  match: "对局",
  opponentDisconnected: "对手断线…",
  rematchOffered: "对手请求再战",

  // review / history
  reviewMode: "回顾模式 · 不可落子",
  toLive: "回到当前",
  navStart: "开局",
  navPrev: "上一步",
  navNext: "下一步",
  navLive: "最新",
  noMovesYet: "尚未落子 · 红先",

  // phone dock — labels sit under a 22px icon, so they must stay short
  dockBack: "退",
  dockFwd: "进",
  dockMore: "更多",
  sheetMore: "本局",
  sheetClose: "关闭",

  // results
  youWin: "你赢了！",
  youLose: "你输了",
  draw: "和棋",
  redWins: "红方胜",
  blackWins: "黑方胜",
  reviewBoard: "查看棋盘",
  reasonCheckmate: "将死",
  reasonStalemate: "困毙 — 无棋可走即负",
  reasonTimeout: "超时",
  reasonResign: "认输",
  reasonAbandon: "对手离开",
  reasonRepetition: "三次重复局面",
  reasonPerpetualCheck: "长将判负 —— 同一局面出现了三次，其间一方每步都在将军；按 WXF 规则，长将不变着的一方判负",
  reasonPerpetualChase: "长捉判负 —— 同一局面出现了三次，其间一方每步都在捉同一个子；按 WXF 规则，长捉不变着的一方判负",
  reasonDraw: "和棋（自然限着）",

  // lessons
  // leaderboard / live game
  offerDraw: "求和",
  drawOfferedTitle: "对手求和",
  drawOfferedBody: "对手提议和棋。",
  accept: "接受",
  decline: "拒绝",
  drawDeclined: "对手拒绝了和棋",
  drawOfferSent: "已发送求和",
  drawOfferWait: "求和已发出，请等对方回应，稍后可以再提。",
  rematchTitle: "再战一局？",
  rematchBody: "对手请求再来一局。",
  connected: "已连接",
  reconnecting: "重新连接中…",
  reasonAgreement: "协议和棋",
  logIn: "登录",
  signUp: "注册",
  logOut: "退出登录",
  username: "用户名",
  usernameOrEmail: "用户名或邮箱",
  usernameRule: "2–16 个字符：中文、字母、数字或下划线（不含中文时至少 3 个）",
  authFillBoth: "请填写用户名和密码。",
  authCreateNamed: "用「{name}」注册新账户 →",
  authErr_username_invalid: "用户名只能用中文、字母、数字或下划线，2–16 个字符（不含中文时至少 3 个）。",
  authErr_email_invalid: "请输入有效的邮箱地址。",
  authErr_password_short: "密码至少需要 8 个字符。",
  authErr_username_taken: "这个用户名已经有人用了，换一个吧。",
  authErr_email_taken: "这个邮箱已经注册过了。",
  authErr_no_account: "没有叫这个名字的账户。",
  authErr_bad_credentials: "用户名或密码不对。",
  authErr_rate_limited: "尝试次数太多，请稍等一会儿再试。",
  email: "电子邮箱",
  password: "密码",
  displayName: "昵称",
  account: "账户",
  signedInAs: "已登录",
  loginTitle: "登录账户",
  signupTitle: "创建账户",
  needAccount: "还没有账户？注册",
  haveAccount: "已有账户？登录",
  // Shown under the sign-up button. Split so the two links can be real <a>
  // elements rather than markup smuggled through a translation string.
  agreePre: "创建账户即表示你同意我们的",
  agreeMid: "和",
  agreePost: "。",
  termsLink: "使用条款",
  privacyLink: "隐私说明",
  continueWithGoogle: "使用 Google 继续",
  orDivider: "或",
  googleCancelled: "登录已取消。",
  playAsGuest: "以访客身份游玩",
  searchingAlone: "暂时没有其他在线棋友。",
  playBotInstead: "改为与电脑对弈",
  elapsed: "已等待",
  analysis: "分析",
  analysisSub: "引擎分析与研究",
  puzzles: "谜题",
  puzzlesSub: "战术训练",
  // the puzzle of the day
  dailyTitle: "每日一题",
  dailySub: "每天一道，全球同题",
  dailyPrompt: "今天的题目。红先行，一步就好。",
  dailyStreak: "连续",
  bestStreak: "最高",
  dailyDone: "解开了。",
  dailyAlready: "今天已经做过了。",
  dailyToday: "今日题目",
  dailyGo: "去做题",
  dailyDoneShort: "今日已完成",
  morePuzzles: "更多题目",
  shareStreak: "分享战绩",
  copied: "已复制。",
  // the five opponents on /play/computer
  botIntro: "五个对手，强度各不相同。卡片上的数字都是引擎的真实参数。",
  botBeginnerName: "新手",
  botBeginnerStyle: "刚学会走棋，只看一步：常常白送棋子，一步杀也会看漏。第一次下象棋，就从这里开始。",
  botEasyName: "学童",
  botEasyStyle: "下得快，看不远，会送子。赢得了新手，就来找它。",
  botMediumName: "茶客",
  botMediumStyle: "稳得住，会拿你的明显失误，但不搞深算。",
  botHardName: "棋士",
  botHardStyle: "能算出几手内的战术。没有计划就难赢。",
  botMasterName: "国手",
  botMasterStyle: "搜得深，几乎不出错。做好输上一阵的准备。",
  plies: "层",
  ply: "层",
  botSlips: "故意走错",
  botNote: "和电脑对弈不计积分，也不需要账号。全程在你的浏览器里运行。",
  resumeBotTitle: "上一盘还没下完",
  resumeBot: "接着下",
  discardBot: "不要了",
  movesLower: "手",
  // the leaderboard
  leaderboardIntro: "按积分排列，取前五十名。只统计积分对局。",
  youTag: "你",
  gamesLower: "局",
  notRankedYet: "你还不在前五十名内。下几盘积分对局就能上榜。",
  // reviewing a finished game
  reviewGame: "复盘分析",
  reviewing: "分析中",
  reviewNote: "在你的浏览器里跑，不上传棋局。一盘棋大约需要半分钟。",
  reviewPickMove: "点一手棋，看看它值多少。",
  avgLoss: "平均损失",
  betterWas: "更好的是",
  cost: "损失",
  verdict_best: "最佳着法",
  verdict_good: "好着",
  verdict_inaccuracy: "不够精确",
  verdict_mistake: "错着",
  verdict_blunder: "漏着",
  evaluation: "局面评估",
  bestMoveLabel: "最佳着法",
  engineOn: "引擎 开",
  engineOff: "引擎 关",
  showBest: "显示提示",
  flip: "翻转",
  puzzlePrompt: "红方走，找出最佳着法",
  correctPuzzle: "正确！",
  wrongPuzzle: "不对，再试一次",
  nextPuzzle: "下一题",
  skip: "跳过",
  streak: "连胜",
  solvedCount: "已解",
  allSolved: "全部完成！稍后再来。",
  theme: "主题",
  leaderboard: "排行榜",
  leaderboardSub: "等级分排名",
  ratedOnly: "仅计入已评分对局",
  noRatedGames: "暂无已评分的对局。",
  rating: "等级分",
  games: "局数",
  gameInProgress: "对局进行中",
  rejoin: "返回对局",
  vs: "对手",
  resignConfirm: "确定认输？",
  resignConfirmBody: "认输后这盘棋就结束了，不能悔棋。",
  closeModal: "关闭",
  leaveGameTitle: "对局仍在进行",
  leaveGameBody: "你可以稍后返回继续，或直接认输。",
  keepPlaying: "继续对局",
  leaveBotBody: "现在离开会结束这局人机对弈。要继续下完，还是退出？",
  exitGame: "退出对局",
  leaveForNow: "暂时离开",
  lessonsTitle: "教程",
  progressDone: "完成",
  allLessons: "课程列表",
  prevLesson: "上一课",
  nextLesson: "下一课",
  reset: "重置",
  taskInfo: "观察棋盘",
  taskExplore: "试一试：点这个棋子，走到任意一个圆点上。",
  lessonExplore: "对，这是它能走的一步。再试试别的圆点，或者进入下一课。",
  taskAnyLegal: "走一步合法棋",
  taskMate: "一步将死",
  taskMove: "走出正确的一步",
  correct: "正确！",
  tryAgain: "再试一次",
  profile: "个人资料",
  profileSub: "你的战绩与对局记录",
  recentGames: "近期对局",
  noGames: "暂无对局记录，快去下一盘吧！",
  signInForProfile: "登录后即可查看战绩与对局记录。",
  watchReplay: "回放",
  memberSince: "注册于",
  wins: "胜",
  losses: "负",
  draws: "和",
  winRate: "胜率",
  replayTitle: "对局回放",
  backToProfile: "返回资料",
  ratedGame: "已评分",
  casualGame: "友谊赛",
  resultRedWin: "红胜",
  resultBlackWin: "黑胜",
  resultDraw: "和棋",
  rCheckmate: "将死",
  rResign: "认输",
  rTimeout: "超时",
  rStalemate: "困毙",
  rAgreement: "协议和棋",
  rAbandonment: "掉线判负",
  rRepetition: "三次重复和棋",
  rPerpetualCheck: "长将判负",
  rPerpetualChase: "长捉判负",
  rDraw: "和棋",
  friends: "好友",
  friendsSub: "添加好友并直接邀请对战",
  addFriend: "添加好友",
  friendUsername: "输入用户名",
  send: "发送",
  requests: "好友请求",
  sentRequests: "已发送的请求",
  challenge: "邀请对战",
  cancelChallenge: "取消邀请",
  remove: "删除",
  online: "在线",
  offline: "离线",
  inGameNow: "对局中",
  noFriends: "还没有好友。用用户名添加吧！",
  waitingForAccept: "等待对方接受…",
  incomingChallenge: "对战邀请",
  challengedYou: "向你发起对战",
  challengeDeclinedMsg: "对方拒绝了你的邀请。",
  challengeCanceledMsg: "邀请已取消或超时。",
  signInForFriends: "登录后即可添加好友并邀请对战。",
  friendAdded: "已添加好友！",
  requestSent: "请求已发送。",
  heroEyebrow: "免费 · 网页即玩",
  heroHead: '掌握千年 <span class="accent">象棋之道</span>',
  heroSub: "与全球棋手对弈，磨炼谋略，在承袭千年传统的现代平台上，体验真正的象棋。",
  playNow: "立即对弈",
  learnXiangqi: "学习象棋",
  chooseMode: "选择模式",
  navPlay: "对弈",
  navLearn: "学习",
  navRank: "排行",
  navProfile: "资料",

  // play hub / lobby
  playTitle: "对弈",
  quickPair: "快速匹配",
  quickPairNote: "选择时限即可开始：系统会把你与该时限下等待的第一位棋手配对。",
  openChallenges: "公开约战",
  openChallengesNote: "发布后可留在本页等待，任何棋手都能接受你的约战。",
  postSeek: "发布约战",
  acceptChallenge: "接受",
  noOpenChallenges: "暂无公开约战 — 不妨发布一个。",
  yourChallenge: "你的约战",
  playersOnline: "人在线",
  gamesInPlay: "局进行中",
  rated: "计分",
  casual: "友谊",
  catBullet: "超快棋",
  catBlitz: "快棋",
  catRapid: "标准",
  catClassical: "慢棋",
  timeControl: "时限",
  seekWaitingNote: "你的约战已在大厅公开，其他棋手可随时接受。",
  botsNotice: "公平提示：当暂时没有真人对手时，你可能会与本站的电脑棋手配对。",
  opponentLeftGame: "对手已离开对局。",
  notifications: "通知",
  record: "战绩",
  rankNovice: "初学",
  rankApprentice: "棋徒",
  rankAdept: "棋士",
  rankExpert: "高手",
  rankMaster: "大师",
  rankGrand: "特级大师",
  profToNext: "距「{tier}」还差 {n} 分",
  profTopRank: "已达最高段位",
  profForm: "近期状态",
  profAll: "全部",
  profTrend: "等级分走势",
  profLast10: "近 10 局分数变化",
  profStreakWin: "连胜",
  profStreakLoss: "连败",
  profNoMatch: "没有符合条件的对局。",
  profStudy: "学习进度",
  profContinue: "继续学习",
  profAsRed: "执红",
  profAsBlack: "执黑",
  noNotifications: "暂无通知。",
  markAllRead: "全部标为已读",
  nFriendRequest: "向你发送了好友请求",
  nFriendAccepted: "接受了你的好友请求",
  nChallenge: "向你发起了对战",
  nMessage: "给你发来消息",
  messages: "私信",
  messagesSub: "与好友聊天",
  noConversations: "还没有对话。去好友页开始聊天吧。",
  writeMessage: "写点什么…",
  sendMessage: "发送",
  messageFriendOnly: "只能给好友发送消息。",
  emailNotifications: "邮件通知",
  emailNotificationsSub: "好友请求、对战邀请和私信会发送邮件提醒。",
  emailUnavailable: "本站尚未配置邮件服务。",
  message: "发消息",
  backToMessages: "返回私信",
  noRatedGamesSub: "排行榜按等级分排名。下完第一盘计分对局，你的名字就会出现在这里。",
  heroBoardAlt: "紫檀嵌金象棋盘",
  signInShort: "需登录",
  moreWays: "其他对弈方式",
  studyOn: "继续精进",
  leaderboardNote: "点击棋手可查看其资料与对局。",

  // learn hub
  learnTitle: "学习",
  learnSub: "教程 · 谜题 · 规则",
  continueLearning: "继续学习",
  trainNow: "开始训练",
  guideTitle: "规则详解",
  guideSub: "棋盘、走法、胜负与记谱",

  // shell: nav menus, home sections, settings, sign-in gate
  menu: "菜单",
  close: "关闭",
  settings: "设置",
  settingsSub: "语言、棋子、主题与音效",
  sound: "音效",
  themeLight: "浅色",
  themeDark: "深色",
  quickMatch: "快速匹配",
  quickMatchSub: "立即为你配对一位在线棋手",
  lobbySub: "浏览并接受公开约战",
  rejoinSub: "你有一局对弈正在进行",
  liveNow: "正在连接大厅…",
  playNowFree: "免费注册开始对弈",
  sectionPlay: "对弈",
  sectionPlaySub: "登录后即可与真人或电脑对弈",
  sectionPlaySubIn: "与真人或电脑对弈",
  sectionLearn: "精进棋艺",
  sectionLearnSub: "无需登录，随时可学",
  sectionCommunity: "棋友",
  sectionCommunitySub: "排名、战绩与好友",
  accountRequired: "对弈需要账户",
  accountRequiredSub: "注册只需十几秒。账户用于记录等级分、对局历史与好友，也让每位棋手都能被公平对待。",
  signInToPlay: "请先登录或注册，即可开始对弈。",
  loginSub: "欢迎回来，继续你的棋局。",
  signupSub: "创建账户，开始你的象棋之路。",
  whyAccount: "为什么需要账户",
  perkRating: "等级分与排行榜排名",
  perkHistory: "完整对局记录与回放",
  perkFriends: "添加好友并直接邀请对战",
  perkProgress: "教程与谜题进度云端同步",
  // pages (titles shown in the browser tab / history)
  brandName: "象棋大师",
  pageHome: "在线象棋",
  notFoundTitle: "页面不存在",
  notFoundBody: "这个地址没有对应的页面。也许链接已过期，或者输错了。",
  resumingGame: "正在恢复对局…",
  liveGame: "对局中",
  quickMatchTitle: "快速配对",
  // board tools
  pieceHints: "棋子提示",
  pieceHintsSub: "显示可走点与被攻击的棋子",
  animations: "落子动画",
  voiceMoves: "语音报着",
  voiceMovesSub: "用中文记谱朗读每一步",
  focusMode: "专注模式",
  focusModeSub: "全屏只看棋盘",
  optOn: "开",
  optOff: "关",
  // account gate
  gateTitle: "此页面需要登录",
  gateBody: "排行榜、个人资料、对局记录与好友都属于账户内容，登录后即可查看。",
  gateWhere: "登录后将前往：",
  signInShortcut: "登录",
  // forgotten password (emailed code)
  forgotLink: "忘记密码？",
  forgotTitle: "重设密码",
  forgotSub: "输入注册时使用的邮箱，我们会发送一个六位验证码。",
  sendCode: "发送验证码",
  codeSent: "验证码已发送",
  codeSentSub: "请查收邮箱 {email}，输入其中的六位验证码。验证码十分钟内有效。",
  codeLabel: "六位验证码",
  verifyCode: "验证",
  resendCode: "重新发送",
  newPasswordTitle: "设置新密码",
  newPasswordSub: "验证通过。请设置新密码，至少八位。",
  newPassword: "新密码",
  confirmPassword: "确认新密码",
  savePassword: "保存新密码",
  passwordsDiffer: "两次输入的密码不一致。",
  passwordShort: "密码至少需要八位。",
  resetDone: "密码已更新，已为你登录。",
  backToLogin: "返回登录",
  checkSpam: "没收到？请查看垃圾邮件文件夹，或稍后重新发送。",
  emailRequired: "请输入邮箱地址。",
  sending: "发送中…",
  resetHowTitle: "重设流程",
  resetStep1: "输入注册邮箱，验证码立即发出。",
  resetStep2: "输入六位验证码——十分钟有效，最多五次机会。",
  resetStep3: "设置新密码。其他设备上的登录会被注销，本次直接登录。",
  // the engine failed and the game carried on regardless
  engineHiccup: "引擎出了点问题，这一步是随手走的。棋局继续。",
  hintFailed: "这一步算不出提示，稍后再试。",
  // the Discord invitation
  discordTitle: "加入我们的社区",
  discordBody: "和棋友聊天、一起复盘，关注每天的开局与残局讨论——新棋局结束后也会自动发到频道里。",
  discordJoin: "加入社区",
  discordNo: "以后再说",
  discordPerk1: "随时和棋友交流",
  discordPerk2: "每日一题与复盘讨论",
  discordPerk3: "对局结果自动推送",
  // confirming the address on a new account
  verifyTitle: "确认邮箱地址",
  verifySub: "我们已向 {email} 发送六位验证码。输入后即可开始对局。",
  verifyCta: "确认邮箱",
  verifyLater: "稍后再说",
  verifyDone: "邮箱已确认——可以开始下棋了。",
  verifyGateTitle: "先确认邮箱",
  verifyGateBody: "与真人对局、发起挑战和棋盘聊天都需要先确认邮箱地址。这样可以挡住用一次性邮箱注册的账号。",
  verifyBannerText: "邮箱尚未确认，暂时无法与真人对局。",
  verifyBannerCta: "立即确认",
  verifyMeanwhile: "在此期间，你仍可以和电脑对战、学习教程、练习谜题。",
  verifyHowTitle: "为什么要确认",
  verifyStep1: "验证码已发往你注册时填写的邮箱。",
  verifyStep2: "输入六位数字——24 小时内有效，最多八次机会。",
  verifyStep3: "确认后即可与真人对局、加好友、使用聊天。",
  verifySent: "验证码已重新发送。",
  verifyNoCode: "没收到？请查看垃圾邮件文件夹，或重新发送。",
  // learn hub
  learnKicker: "学 · 象棋学堂",
  learnHeadline: "从走法到杀法，一步一步来",
  learnBlurb: "十七节互动教程、战术谜题与引擎分析——都在棋盘上边走边学，不只是文字。",
  yourProgress: "你的进度",
  lessonsLabel: "教程",
  puzzlesLabel: "谜题",
  nextUp: "接着学",
  allDoneReview: "全部完成 · 复习",
  pathTitle: "学习路径",
  pathSub: "五个阶段，循序渐进",
  toolsTitle: "学习工具",
  toolsSub: "学完之后，用它们继续打磨",
  boardThemeSub: "二十七种棋盘，随时切换",
  groupAppearance: "外观",
  groupAppearanceSub: "棋子、主题与语言",
  groupPlay: "对局",
  groupPlaySub: "下棋时棋盘的行为",
  groupAccount: "账户",
  groupAccountSub: "邮件与通知",
  whyCheckOnly: "将军了，但对方能应；这一步没有形成杀。再想想。",
  whyHangs: "走完这步，{piece}会立刻被吃掉。",
  whyWinsButNot: "吃到了子，但这不是本课要的着法。",
  whyQuiet: "合法，但没有完成任务——对方有充裕的时间应对。",
  pieceKing: "将",
  pieceAdvisor: "士",
  pieceElephant: "象",
  pieceHorse: "马",
  pieceRook: "车",
  pieceCannon: "炮",
  pievePawn: "兵",
  quizTitle: "检查一下",
  quizRight: "答对了",
  quizWrong: "再想想",
  stepOf: "第 {n} 步 / 共 {t} 步",
  continueStep: "继续",
  chatTitle: "聊天",
  chatPlaceholder: "说点什么…",
  isTyping: "{name} 正在输入…",
  settingsIntro: "选好后点右上角的「保存」才会生效；不保存就离开，一切恢复原样。",
  settingsSave: "保存",
  settingsSaved: "已保存",
  settingsUnsaved: "有未保存的更改",
  dPieces: "棋子字形：繁体、简体、字母或符号",
  dTheme: "界面明暗",
  dScene: "棋盘背后的景色与天气",
  dLanguage: "整个界面的语言",
  dHints: "选中棋子时显示可走点，并标记被攻击的子",
  dAnim: "走子时的滑动动画",
  dSound: "落子、将军与结束的音效",
  dVoice: "用语音朗读每一步",
  dEmail: "对局邀请与好友请求的邮件提醒",
  dailyReminders: "课程邮件",
  dDaily: "每三天一封：一节课、一道题和一篇好文章",
  dBoardTheme: "二十七种棋盘，随时切换，对局中也可以",
  megaStart: "开始对局",
  megaPeople: "棋友",
  megaTrain: "训练",
  megaReference: "参考",
  boardTheme: "棋盘样式",
  refTitle: "规则百科",
  refSub: "每条规则都有专页，配可动棋盘",
  refPieces: "棋子走法大全",
  refPiecesSub: "七种棋子，逐一讲解",
  refFlying: "白脸将",
  refFlyingSub: "将帅不可照面的规则",
  refRepetition: "长将与长捉",
  refRepetitionSub: "按 WXF 标准判定重复局面",
  refOpenings: "开局大全",
  refOpeningsSub: "中炮、屏风马及其思路",
  refAll: "浏览完整规则百科",
  startLesson: "开始这一课",
  resumeLesson: "继续这一课",
  groupComplete: "已完成",
  groupLessons: "课",
  groupLesson: "课",
  howTitle: "这样学最快",
  howStep1: "先看走法",
  howStep1Sub: "八节基础课把每个子的走法与限制讲清楚，全部可以在棋盘上试。",
  howStep2: "再练战术",
  howStep2Sub: "捉双、牵制、一步杀——把看得懂变成走得出。",
  howStep3: "最后复盘",
  howStep3Sub: "用分析棋盘复盘自己的对局，引擎会告诉你哪一步走偏了。",
  minLesson: "约 2 分钟",
  lessonsAllDone: "所有教程都已完成 —— 去谜题练手，或用分析棋盘复盘。",

  // ---- 观战 ----
  watchTitle: "观战",
  watchSub: "看正在进行的对局",
  watchGameTitle: "观战中",
  watchLoading: "正在进入观战…",
  gamesInPlayTitle: "正在进行的对局",
  watchNote: "这里列出公开对局。用房间口令开的私人对局不会出现在这里，只有对局者分享链接才能观看。",
  noLiveGames: "此刻无人对弈",
  noLiveGamesSub: "开一局，或发一条求战——下一个被围观的就是你。",
  watching: "人在看",
  watchingReadOnly: "观战模式：可以聊天，不能走子。",
  watchLink: "观战链接",
  shareThisGame: "分享此局",
  copyWatchLink: "复制观战链接",
  watchGoneTitle: "这局已经结束了",
  watchGoneBody: "对局结束后就不能再观战了。如果是公开对局，可以看棋谱回放。",
  moveNo: "第",

  // ---- 推送提醒 ----
  pushNotifications: "浏览器推送",
  dPush: "关闭网页时，好友请求与挑战直接推送到设备",
  pushEnable: "开启",
  pushBlocked: "已被浏览器拦截",
  pushBlockedNote: "需要在浏览器的网站设置里重新允许通知。",
  pushUnsupported: "此浏览器不支持推送通知。",
  pushOnNote: "本设备已开启。网页在前台时不会重复提醒。",
  pushFailed: "开启失败，请稍后再试。",
  pushServerOff: "服务器暂未启用推送。",
};

const EN: Dict = {
  player: "Player",
  nickname: "Nickname",
  language: "Language",
  scene: "Scene",
  sceneDay: "Day",
  sceneNight: "Night",
  weatherClear: "Clear",
  weatherRain: "Rain",
  weatherSnow: "Snow",
  pieces: "Pieces",
  piecesTrad: "Chinese",
  piecesSimp: "Simplified",
  piecesWestern: "Letters",
  piecesSymbol: "Symbols",
  soundOn: "🔊 Sound",
  soundOff: "🔇 Muted",
  playBot: "Play vs Bot",
  playBotSub: "Strong engine · 5 levels · hints",
  playOnline: "Play Online",
  playOnlineSub: "Instant match against a stranger",
  playFriends: "Play with Friends",
  playFriendsSub: "Private room, share a link",
  lessons: "Lessons",
  lessonsSub: "Learn xiangqi step by step",
  homeNote: "Drag or click a piece to move. Pieces under attack are marked.",

  botTitle: "Choose your opponent",
  difficulty: "Difficulty",
  yourSide: "Your side",
  beginner: "Beginner",
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
  master: "Master",
  sideRed: "Red",
  sideBlack: "Black",
  sideRandom: "Random",
  back: "Back",
  start: "Start",

  friendsTitle: "Play with Friends",
  friendsDesc: "Create a room and send the link to a friend, or enter their room code.",
  createRoom: "Create Room",
  roomCodePlaceholder: "ROOM CODE",
  join: "Join",
  searching: "Searching for an opponent…",
  creating: "Creating room…",
  joining: "Joining room",
  cancel: "Cancel",
  /* feedback */
  fbTitle: "Send feedback",
  fbFromGame: "Something wrong with that game? Tell us",
  fbSub: "Tell us what broke, what is missing, or what you liked. We read all of it.",
  fbOpen: "Write to us",
  fbWhat: "Your message",
  fbBug: "Something broke",
  fbIdea: "An idea",
  fbPraise: "Something good",
  fbOther: "Other",
  fbPlaceholder: "What happened, and what did you expect instead?",
  fbEmailHint: "Email, if you would like a reply (optional)",
  fbSend: "Send",
  fbSending: "Sending...",
  fbThanks: "Thank you — we have it.",
  fbEmpty: "Please write something first.",
  fbFailed: "That did not send. Your message is still here — try again.",
  fbDiscordTitle: "Join our Discord community",
  fbDiscordText: "Chat with other players, and tell us about problems directly.",
  fbDiscordJoin: "Join",
  /* the timed prompt, and the rating it asks for */
  fbAskTitle: "Have you got half a minute?",
  fbAskSub: "We would like to know how this is going for you — what works, what does not. Under 30 seconds.",
  fbAskYes: "Send feedback",
  fbAskLater: "Not now",
  fbAskNever: "No, thanks",
  fbRate: "How is it going overall?",
  fbRateSkip: "(optional)",
  fbRate1: "Poor",
  fbRate5: "Great",
  fbImprove: "What should we improve?",
  groupFeedback: "Feedback",
  groupFeedbackSub: "Tell us what is wrong or missing",
  roomLabel: "Room",
  shareLink: "Share this link with your friend:",
  copy: "Copy",
  copiedShort: "Copied",
  chatEmpty: "No messages yet. Say hello.",
  watchingCount: "{n} watching",
  waitingOpponent: "Waiting for opponent…",

  redToMove: "Red to move",
  blackToMove: "Black to move",
  gameOver: "Game over",
  yourMove: "Your move",
  opponentMove: "Opponent's move",
  botThinking: "Bot thinking…",
  check: "Check!",
  // the warning before a repetition loses the game (apps/web/src/repwarn.ts)
  repWarnCheckShort: "Perpetual check: vary your move",
  repWarnChaseShort: "Perpetual chase: vary your move",
  repWarnCheck: "Perpetual check: you have checked on every move and the position is repeating. If it repeats once more, you lose (WXF rule). Play a different move.",
  repWarnChase: "Perpetual chase: you have chased the same piece on every move and the position is repeating. If it repeats once more, you lose (WXF rule). Play a different move.",
  hint: "Hint",
  hintThinking: "Finding a hint…",
  undo: "Undo",
  newGame: "New",
  home: "Home",
  resign: "Resign",
  rematch: "Rematch",
  movesTitle: "Moves",
  you: "You",
  bot: "Bot",
  match: "Match",
  opponentDisconnected: "Opponent disconnected…",
  rematchOffered: "Opponent wants a rematch",

  reviewMode: "Review mode · moves disabled",
  toLive: "Back to live",
  navStart: "Start",
  navPrev: "Previous",
  navNext: "Next",
  navLive: "Latest",
  noMovesYet: "No moves yet — Red opens",

  dockBack: "Back",
  dockFwd: "Fwd",
  dockMore: "More",
  sheetMore: "This table",
  sheetClose: "Close",

  youWin: "You win!",
  youLose: "You lose",
  draw: "Draw",
  redWins: "Red wins",
  blackWins: "Black wins",
  reviewBoard: "Review board",
  reasonCheckmate: "Checkmate",
  reasonStalemate: "Stalemate — no legal move is a loss",
  reasonTimeout: "Time out",
  reasonResign: "Resignation",
  reasonAbandon: "Opponent left",
  reasonRepetition: "Threefold repetition",
  reasonPerpetualCheck: "Perpetual check — the same position came up three times while one side checked on every move, and under the WXF rule that side loses",
  reasonPerpetualChase: "Perpetual chase — the same position came up three times while one side chased the same piece on every move, and under the WXF rule that side loses",
  reasonDraw: "Draw (60-move rule)",

  offerDraw: "Offer draw",
  drawOfferedTitle: "Draw offer",
  drawOfferedBody: "Your opponent offers a draw.",
  accept: "Accept",
  decline: "Decline",
  drawDeclined: "Draw declined",
  drawOfferSent: "Draw offer sent",
  drawOfferWait: "Your draw offer is out. Wait for an answer; you can offer again shortly.",
  rematchTitle: "Rematch?",
  rematchBody: "Your opponent wants a rematch.",
  connected: "Connected",
  reconnecting: "Reconnecting…",
  reasonAgreement: "Draw agreed",
  logIn: "Log in",
  signUp: "Sign up",
  logOut: "Log out",
  username: "Username",
  usernameOrEmail: "Username or email",
  usernameRule: "2–16 characters: Chinese, letters, numbers or _ (at least 3 without Chinese)",
  authFillBoth: "Please enter your username and password.",
  authCreateNamed: "Create an account called “{name}” →",
  authErr_username_invalid: "Usernames use Chinese characters, letters, numbers or _, 2–16 characters (at least 3 without Chinese).",
  authErr_email_invalid: "Please enter a valid email address.",
  authErr_password_short: "Password must be at least 8 characters.",
  authErr_username_taken: "That username is taken. Try another.",
  authErr_email_taken: "That email is already registered.",
  authErr_no_account: "There is no account with that username.",
  authErr_bad_credentials: "Wrong username or password.",
  authErr_rate_limited: "Too many attempts. Wait a moment and try again.",
  email: "Email",
  password: "Password",
  displayName: "Display name",
  account: "Account",
  signedInAs: "Signed in as",
  loginTitle: "Log in",
  signupTitle: "Create account",
  needAccount: "No account? Sign up",
  haveAccount: "Have an account? Log in",
  agreePre: "By creating an account you agree to our ",
  agreeMid: " and ",
  agreePost: ".",
  termsLink: "Terms of Use",
  privacyLink: "Privacy Notice",
  continueWithGoogle: "Continue with Google",
  orDivider: "or",
  googleCancelled: "Sign-in cancelled.",
  playAsGuest: "Play as guest",
  searchingAlone: "No one else is online right now.",
  playBotInstead: "Play the computer instead",
  elapsed: "waiting",
  analysis: "Analysis",
  analysisSub: "Engine analysis & study",
  puzzles: "Puzzles",
  puzzlesSub: "Train your tactics",
  // the puzzle of the day
  dailyTitle: "Daily puzzle",
  dailySub: "One a day, the same one for everybody",
  dailyPrompt: "Today's position. Red to move — one move is all it takes.",
  dailyStreak: "Streak",
  bestStreak: "Best",
  dailyDone: "Solved.",
  dailyAlready: "You had already done today's.",
  dailyToday: "Today's puzzle",
  dailyGo: "Solve it",
  dailyDoneShort: "Done today",
  morePuzzles: "More puzzles",
  shareStreak: "Share",
  copied: "Copied.",
  // the five opponents on /play/computer
  botIntro: "Five opponents, none of them the same. Every number on a card is a real setting from the engine.",
  botBeginnerName: "The Newcomer",
  botBeginnerStyle: "Has only just learned the moves. Looks one move ahead, often leaves pieces hanging and can miss a mate in one. Start here if xiangqi is new to you.",
  botEasyName: "The Student",
  botEasyStyle: "Moves quickly, does not look far, and gives pieces away. The next step once you can beat the Newcomer.",
  botMediumName: "The Teahouse Regular",
  botMediumStyle: "Solid. Punishes an obvious mistake, but will not find anything deep.",
  botHardName: "The Club Player",
  botHardStyle: "Sees tactics several moves out. You will need a plan, not just good moves.",
  botMasterName: "The Master",
  botMasterStyle: "Searches deeply and almost never errs. Expect to lose for a while.",
  plies: "plies",
  ply: "ply",
  botSlips: "deliberate slips",
  botNote: "Games against the computer are unrated and need no account. Everything runs in your browser.",
  resumeBotTitle: "You have a game in progress",
  resumeBot: "Resume",
  discardBot: "Discard",
  movesLower: "moves",
  // the leaderboard
  leaderboardIntro: "Ranked by rating, top fifty. Rated games only.",
  youTag: "you",
  gamesLower: "games",
  notRankedYet: "You are not in the top fifty yet. A few rated games is all it takes.",
  // reviewing a finished game
  reviewGame: "Review this game",
  reviewing: "Reviewing",
  reviewNote: "Runs in your browser; the game is not uploaded. About half a minute for a full game.",
  reviewPickMove: "Pick a move to see what it cost.",
  avgLoss: "Avg loss",
  betterWas: "Better was",
  cost: "cost",
  verdict_best: "Best move",
  verdict_good: "Good",
  verdict_inaccuracy: "Inaccuracy",
  verdict_mistake: "Mistake",
  verdict_blunder: "Blunder",
  evaluation: "Evaluation",
  bestMoveLabel: "Best move",
  engineOn: "Engine on",
  engineOff: "Engine off",
  showBest: "Show hint",
  flip: "Flip",
  puzzlePrompt: "Red to move — find the best move",
  correctPuzzle: "Solved!",
  wrongPuzzle: "Not quite — try again",
  nextPuzzle: "Next puzzle",
  skip: "Skip",
  streak: "Streak",
  solvedCount: "Solved",
  allSolved: "All done! Come back later.",
  theme: "Theme",
  leaderboard: "Leaderboard",
  leaderboardSub: "Ranked by rating",
  ratedOnly: "Rated games only",
  noRatedGames: "No rated games yet.",
  rating: "Rating",
  games: "Games",
  gameInProgress: "Game in progress",
  rejoin: "Rejoin game",
  vs: "vs",
  resignConfirm: "Resign this game?",
  resignConfirmBody: "That ends the game. It cannot be taken back.",
  closeModal: "Close",
  leaveGameTitle: "Your game is still running",
  leaveGameBody: "You can come back to it later, or resign now.",
  keepPlaying: "Keep playing",
  leaveBotBody: "Leaving now ends this game against the computer. Keep playing, or exit?",
  exitGame: "Exit game",
  leaveForNow: "Leave for now",
  lessonsTitle: "Lessons",
  progressDone: "done",
  allLessons: "All lessons",
  prevLesson: "Previous",
  nextLesson: "Next",
  reset: "Reset",
  taskInfo: "Study the board",
  taskExplore: "Try it: tap the piece and move it to any of the dots.",
  lessonExplore: "Yes, that's one of its moves. Try another dot, or go on to the next lesson.",
  taskAnyLegal: "Play any legal move",
  taskMate: "Find mate in one",
  taskMove: "Find the move",
  correct: "Correct!",
  tryAgain: "Not quite — try again.",
  profile: "Profile",
  profileSub: "Your record & game history",
  recentGames: "Recent games",
  noGames: "No games yet — go play one!",
  signInForProfile: "Sign in to see your record and game history.",
  watchReplay: "Replay",
  memberSince: "Member since",
  wins: "Wins",
  losses: "Losses",
  draws: "Draws",
  winRate: "Win rate",
  replayTitle: "Game replay",
  backToProfile: "Back to profile",
  ratedGame: "Rated",
  casualGame: "Casual",
  resultRedWin: "Red wins",
  resultBlackWin: "Black wins",
  resultDraw: "Draw",
  rCheckmate: "checkmate",
  rResign: "resignation",
  rTimeout: "timeout",
  rStalemate: "stalemate",
  rAgreement: "agreement",
  rAbandonment: "abandonment",
  rRepetition: "threefold repetition",
  rPerpetualCheck: "perpetual check",
  rPerpetualChase: "perpetual chase",
  rDraw: "draw",
  friends: "Friends",
  friendsSub: "Add friends & challenge them directly",
  addFriend: "Add friend",
  friendUsername: "Enter a username",
  send: "Send",
  requests: "Friend requests",
  sentRequests: "Sent requests",
  challenge: "Challenge",
  cancelChallenge: "Cancel",
  remove: "Remove",
  online: "Online",
  offline: "Offline",
  inGameNow: "In game",
  noFriends: "No friends yet — add one by username!",
  waitingForAccept: "Waiting for them to accept…",
  incomingChallenge: "Game challenge",
  challengedYou: "challenges you to a game",
  challengeDeclinedMsg: "Your challenge was declined.",
  challengeCanceledMsg: "The challenge was canceled or timed out.",
  signInForFriends: "Sign in to add friends and challenge them.",
  friendAdded: "Friend added!",
  requestSent: "Request sent.",
  heroEyebrow: "Free · Plays in your browser",
  heroHead: 'Master the Ancient Art of <span class="accent">Xiangqi</span>',
  heroSub: "Challenge players worldwide, refine your strategy, and experience Xiangqi on a platform that honours centuries of tradition with modern technology.",
  playNow: "Play Now",
  learnXiangqi: "Learn Xiangqi",
  chooseMode: "Choose your mode",
  navPlay: "Play",
  navLearn: "Learn",
  navRank: "Ranks",
  navProfile: "Profile",

  playTitle: "Play",
  quickPair: "Quick pair",
  quickPairNote: "Pick a time control and you are paired with the first player waiting in that pool.",
  openChallenges: "Open challenges",
  openChallengesNote: "Post one and stay on this page — any player can accept it.",
  postSeek: "Post a challenge",
  acceptChallenge: "Accept",
  noOpenChallenges: "No open challenges yet — post one.",
  yourChallenge: "Your challenge",
  playersOnline: "online",
  gamesInPlay: "in play",
  rated: "Rated",
  casual: "Casual",
  catBullet: "Bullet",
  catBlitz: "Blitz",
  catRapid: "Rapid",
  catClassical: "Classical",
  timeControl: "Time control",
  seekWaitingNote: "Your challenge is listed in the lobby — anyone can accept it.",
  botsNotice: "Fair play: when no human opponent is free, you may be matched with one of our computer opponents.",
  opponentLeftGame: "Your opponent has left the game.",
  notifications: "Notifications",
  record: "Record",
  rankNovice: "Novice",
  rankApprentice: "Apprentice",
  rankAdept: "Adept",
  rankExpert: "Expert",
  rankMaster: "Master",
  rankGrand: "Grandmaster",
  profToNext: "{n} to {tier}",
  profTopRank: "Highest rank reached",
  profForm: "Recent form",
  profAll: "All",
  profTrend: "Rating trend",
  profLast10: "Rating, last 10 games",
  profStreakWin: "Win streak",
  profStreakLoss: "Loss streak",
  profNoMatch: "No games match this filter.",
  profStudy: "Study progress",
  profContinue: "Continue learning",
  profAsRed: "Playing Red",
  profAsBlack: "Playing Black",
  noNotifications: "Nothing new.",
  markAllRead: "Mark all read",
  nFriendRequest: "sent you a friend request",
  nFriendAccepted: "accepted your friend request",
  nChallenge: "challenged you to a game",
  nMessage: "sent you a message",
  messages: "Messages",
  messagesSub: "Chat with your friends",
  noConversations: "No conversations yet — start one from your friends list.",
  writeMessage: "Write a message…",
  sendMessage: "Send",
  messageFriendOnly: "You can only message friends.",
  emailNotifications: "Email notifications",
  emailNotificationsSub: "Friend requests, challenges and messages are emailed to you.",
  emailUnavailable: "Email is not configured on this server.",
  message: "Message",
  backToMessages: "Back to messages",
  noRatedGamesSub: "The board ranks players by rating. Finish one rated game and your name appears here.",
  heroBoardAlt: "A carved rosewood xiangqi board, set for the opening move",
  signInShort: "Sign in",
  moreWays: "More ways to play",
  studyOn: "Keep studying",
  leaderboardNote: "Tap a player to open their profile.",

  learnTitle: "Learn",
  learnSub: "Lessons · puzzles · rules",
  continueLearning: "Continue",
  trainNow: "Train now",
  guideTitle: "Rules & notation",
  guideSub: "Board, moves, results and notation",

  menu: "Menu",
  close: "Close",
  settings: "Settings",
  settingsSub: "Language, pieces, theme and sound",
  sound: "Sound",
  themeLight: "Light",
  themeDark: "Dark",
  quickMatch: "Quick match",
  quickMatchSub: "Pair me with someone online right now",
  lobbySub: "Browse and accept open challenges",
  rejoinSub: "You have a game in progress",
  liveNow: "Connecting to the lobby…",
  playNowFree: "Create a free account",
  sectionPlay: "Play",
  sectionPlaySub: "Sign in to face a person or the engine",
  sectionPlaySubIn: "Face a person or the engine",
  sectionLearn: "Improve",
  sectionLearnSub: "Open to everyone — no account needed",
  sectionCommunity: "Community",
  sectionCommunitySub: "Rankings, records and friends",
  accountRequired: "An account is required to play",
  accountRequiredSub: "It takes about fifteen seconds. Your account carries your rating, your game history and your friends — and it keeps games fair for everyone at the board.",
  signInToPlay: "Sign in or create an account to start playing.",
  loginSub: "Welcome back — pick up where you left off.",
  signupSub: "Create an account and start your xiangqi journey.",
  whyAccount: "Why an account",
  perkRating: "A rating and a place on the leaderboard",
  perkHistory: "Every game saved, with full replay",
  perkFriends: "Add friends and challenge them directly",
  perkProgress: "Lesson and puzzle progress synced",
  // pages (titles shown in the browser tab / history)
  brandName: "Xiangqi Master",
  pageHome: "Play Xiangqi online",
  notFoundTitle: "Page not found",
  notFoundBody: "There is nothing at this address — the link may have expired, or been mistyped.",
  resumingGame: "Resuming your game…",
  liveGame: "Your game",
  quickMatchTitle: "Quick match",
  // board tools
  pieceHints: "Piece hints",
  pieceHintsSub: "Legal-move dots and marks on pieces under attack",
  animations: "Move animation",
  voiceMoves: "Spoken moves",
  voiceMovesSub: "Reads each move out loud as it is played",
  focusMode: "Focus mode",
  focusModeSub: "Fullscreen board, nothing else",
  optOn: "On",
  optOff: "Off",
  // account gate
  gateTitle: "This page needs an account",
  gateBody: "Rankings, profiles, game history and friends all live inside an account. Sign in and you will land right back here.",
  gateWhere: "After signing in you'll go to:",
  signInShortcut: "Sign in",
  // forgotten password (emailed code)
  forgotLink: "Forgot your password?",
  forgotTitle: "Reset your password",
  forgotSub: "Enter the email address on your account and we'll send you a six-digit code.",
  sendCode: "Email me a code",
  codeSent: "Code sent",
  codeSentSub: "Check {email} and type the six-digit code. It is good for ten minutes.",
  codeLabel: "Six-digit code",
  verifyCode: "Verify code",
  resendCode: "Send another",
  newPasswordTitle: "Choose a new password",
  newPasswordSub: "Code accepted. Pick a new password — at least eight characters.",
  newPassword: "New password",
  confirmPassword: "Repeat new password",
  savePassword: "Save new password",
  passwordsDiffer: "Those two passwords do not match.",
  passwordShort: "Password must be at least 8 characters.",
  resetDone: "Password updated — you are signed in.",
  backToLogin: "Back to log in",
  checkSpam: "Nothing yet? Check your spam folder, or send another in a minute.",
  emailRequired: "Enter your email address.",
  sending: "Sending…",
  resetHowTitle: "How this works",
  resetStep1: "Give us the address on your account and the code goes out at once.",
  resetStep2: "Type the six digits — good for ten minutes, five attempts.",
  resetStep3: "Choose a new password. Other devices are signed out; this one stays in.",
  // the engine failed and the game carried on regardless
  engineHiccup: "The engine stumbled there, so that move was a quick one. The game carries on.",
  hintFailed: "No hint for this position — try again in a moment.",
  // the Discord invitation
  // "Community", not "play": games are played on the site, not on Discord.
  discordTitle: "Join our community",
  discordBody: "Chat with other players, go over your games together, and follow the daily opening and endgame talk — finished games land in the channel automatically.",
  discordJoin: "Join the community",
  discordNo: "Maybe later",
  discordPerk1: "Talk xiangqi with other players",
  discordPerk2: "A puzzle a day, and post-mortems",
  discordPerk3: "Every finished game posted",
  // confirming the address on a new account
  verifyTitle: "Confirm your email address",
  verifySub: "We sent a six-digit code to {email}. Enter it and you can start playing.",
  verifyCta: "Confirm email",
  verifyLater: "Not now",
  verifyDone: "Address confirmed — you're clear to play.",
  verifyGateTitle: "Confirm your email first",
  verifyGateBody: "Playing another person, sending a challenge and the chat beside the board all need a confirmed address. It is what keeps throwaway sign-ups out of the games.",
  verifyBannerText: "Your email isn't confirmed yet, so games against people are on hold.",
  verifyBannerCta: "Confirm now",
  verifyMeanwhile: "In the meantime you can still play the computer, work through the lessons and solve puzzles.",
  verifyHowTitle: "Why this step",
  verifyStep1: "The code goes to the address you signed up with.",
  verifyStep2: "Type the six digits — good for 24 hours, eight attempts.",
  verifyStep3: "Once it's in, human games, friends and chat all open up.",
  verifySent: "Another code is on its way.",
  verifyNoCode: "Nothing yet? Check your spam folder, or send another.",
  // learn hub
  learnKicker: "Study · The academy",
  learnHeadline: "From how the pieces move to how the game is won",
  learnBlurb: "Seventeen interactive lessons, tactical puzzles and an engine to check your own games — all played out on a real board, not read off a page.",
  yourProgress: "Your progress",
  lessonsLabel: "Lessons",
  puzzlesLabel: "Puzzles",
  nextUp: "Next up",
  allDoneReview: "All done — review",
  pathTitle: "The path",
  pathSub: "Five stages, in the order that makes sense",
  toolsTitle: "Study tools",
  toolsSub: "For when the lessons are behind you",
  boardThemeSub: "Twenty-seven boards. Switch any time, mid-game included.",
  groupAppearance: "Appearance",
  groupAppearanceSub: "Pieces, theme and language",
  groupPlay: "Playing",
  groupPlaySub: "How the board behaves during a game",
  groupAccount: "Account",
  groupAccountSub: "Email and notifications",
  whyCheckOnly: "That gives check, but the general has an answer and the attack runs out. Look for something forcing.",
  whyHangs: "After that move the {piece} can be captured straight away.",
  whyWinsButNot: "It wins material, but it is not the move this lesson is about.",
  whyQuiet: "Legal, but it does not achieve the task — it gives the defender time to consolidate.",
  pieceKing: "general",
  pieceAdvisor: "advisor",
  pieceElephant: "elephant",
  pieceHorse: "horse",
  pieceRook: "chariot",
  pieceCannon: "cannon",
  pievePawn: "soldier",
  quizTitle: "Check yourself",
  quizRight: "Correct",
  quizWrong: "Not quite",
  stepOf: "Step {n} of {t}",
  continueStep: "Continue",
  chatTitle: "Chat",
  chatPlaceholder: "Say something…",
  isTyping: "{name} is typing…",
  settingsIntro: "Try things out, then press Save at the top right to keep them. Leave without saving and everything goes back.",
  settingsSave: "Save",
  settingsSaved: "Saved",
  settingsUnsaved: "Unsaved changes",
  dPieces: "Traditional, simplified, letters or symbols",
  dTheme: "Light or dark interface",
  dScene: "The scene and weather behind the board",
  dLanguage: "The language of the whole interface",
  dHints: "Show legal moves for a selected piece, and mark pieces under attack",
  dAnim: "Slide pieces when they move",
  dSound: "Move, check and game-end sounds",
  dVoice: "Read each move aloud",
  dEmail: "Email me about game invites and friend requests",
  dailyReminders: "Lesson email",
  dDaily: "Every 3 days: one lesson, one puzzle and one thing worth reading",
  dBoardTheme: "Twenty-seven boards. Switch any time, mid-game included.",
  megaStart: "Start a game",
  megaPeople: "People",
  megaTrain: "Train",
  megaReference: "Reference",
  boardTheme: "Board style",
  refTitle: "Rules reference",
  refSub: "Every rule on its own page, with a board you can move",
  refPieces: "How every piece moves",
  refPiecesSub: "All seven, one at a time",
  refFlying: "The flying general",
  refFlyingSub: "Why generals may never face each other",
  refRepetition: "Perpetual check & chase",
  refRepetitionSub: "How repetition is judged, to the WXF standard",
  refOpenings: "Opening reference",
  refOpeningsSub: "Central Cannon, Screen Horses and the ideas behind them",
  refAll: "Browse the full reference",
  startLesson: "Start this lesson",
  resumeLesson: "Continue this lesson",
  groupComplete: "Complete",
  groupLessons: "lessons",
  groupLesson: "lesson",
  howTitle: "How to get good, fast",
  howStep1: "Learn the moves",
  howStep1Sub: "Eight short lessons cover every piece and the rules that trip up chess players — each one playable on the board.",
  howStep2: "Drill the tactics",
  howStep2Sub: "Forks, pins and mate in one: the patterns that decide club games, until you spot them without looking.",
  howStep3: "Review your games",
  howStep3Sub: "Replay your own games on the analysis board and let the engine show you the move that lost it.",
  minLesson: "about 2 min",
  lessonsAllDone: "Every lesson is done — take the puzzles next, or review a game on the analysis board.",

  // ---- watching ----
  watchTitle: "Watch",
  watchSub: "Follow a game that is being played right now",
  watchGameTitle: "Watching",
  watchLoading: "Joining the game…",
  gamesInPlayTitle: "Games in play",
  watchNote: "Public games are listed here. A game started from a private room code never is — only a link one of the players shares will open it.",
  noLiveGames: "Nobody is playing right now",
  noLiveGamesSub: "Start a game or post an open challenge, and you will be the one being watched.",
  watching: "watching",
  watchingReadOnly: "Watching: you can talk at the table, but not move.",
  watchLink: "Watch link",
  shareThisGame: "Share this game",
  copyWatchLink: "Copy the watch link",
  watchGoneTitle: "That game has finished",
  watchGoneBody: "A game can only be watched while it is being played. If it was a public game, the replay is still there.",
  moveNo: "move",

  // ---- push notifications ----
  pushNotifications: "Push notifications",
  dPush: "Friend requests and challenges on your device, even with the site closed",
  pushEnable: "Turn on",
  pushBlocked: "Blocked",
  pushBlockedNote: "Notifications are blocked for this site — allow them in your browser's site settings.",
  pushUnsupported: "This browser cannot show push notifications.",
  pushOnNote: "On for this device. Nothing is shown while you are looking at the site.",
  pushFailed: "That did not work. Try again in a moment.",
  pushServerOff: "Push is not switched on for this server yet.",
};

export function t(key: keyof typeof EN | string): string {
  const d = lang === "zh" ? ZH : lang === "vi" ? VI : EN;
  return d[key] ?? EN[key] ?? String(key);
}

/** Pick the right half of a bilingual content pair (used by lesson data).
 *  A pair may carry a `vi` half; without one a Vietnamese reader gets English. */
export function pick(pair: { zh: string; en: string; vi?: string }): string {
  if (lang === "zh") return pair.zh;
  if (lang === "vi") return pair.vi ?? pair.en;
  return pair.en;
}
